import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { cancelPlotThunk } from '../store/slices/plotCancellationSlice'
import { getActiveInstallmentsByBooking } from '../api'
import {
  ArrowLeft,
  Calendar,
  Check,
  ChevronRight,
  Clock,
  FileText,
  Loader2,
  X,
  Search,
  DollarSign,
  PieChart,
  User,
  MapPin,
  AlertCircle
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

const CancelPlotForm = ({
  onClose,
  themeColor,
  activeSociety,
  bookings = [],
  users = []
}) => {
  const dispatch = useDispatch()

  // Wizard State
  const [step, setStep] = useState(1) // 1: Search, 2: Refund Logic, 3: Review
  const [direction, setDirection] = useState(0) // Animation direction

  // Data State
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [completedInstallments, setCompletedInstallments] = useState([])
  const [loadingInstallments, setLoadingInstallments] = useState(false)
  const [filteredBookings, setFilteredBookings] = useState([])
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [error, setError] = useState(null)
  const searchTimeoutRef = useRef(null)

  // Form Data
  const [formData, setFormData] = useState({
    refundType: 'full', // 'full' or 'installment'
    societyDeductionType: 'percentage', // 'percentage' or 'fixed'
    societyDeductionPercentage: 0,
    societyDeductionAmount: 0,
    refundYears: 0,
    refundMonths: 0,
    initialRefundAmount: 0,
    cancellationReason: '',
    otherReason: ''
  })

  // Calculations State
  const [refundDetails, setRefundDetails] = useState({
    totalPaid: 0,
    dealerCommission: 0,
    societyDeduction: 0,
    netRefundAmount: 0,
    immediateRefund: 0,
    remainingBalance: 0,
    monthlyInstallment: 0
  })

  // --- LOGIC: Filtering & Search ---
  useEffect(() => {
    if (bookings.length && activeSociety?._id) {
      const societyBookings = bookings.filter(booking => {
        const plot = booking.plot_id;
        
        // Exclude if plot is not found
        if (!plot) return false;
        
        // Exclude if plot doesn't belong to active society
        if (!plot.society_id || plot.society_id.toString() !== activeSociety._id.toString()) {
          return false;
        }
        
        // Exclude if plot is already available (not booked)
        if (plot.status === 'Available' || plot.booking_status === 'Available') {
          return false;
        }
        
        // Only include active bookings (Booked, Sold, Confirmed, etc.) - exclude Cancelled
        const validBookingStatuses = ['Booked', 'Sold', 'Confirmed', 'Pending'];
        if (!validBookingStatuses.includes(booking.booking_status)) {
          return false;
        }
        
        // Exclude if booking is already cancelled
        if (booking.booking_status === 'Cancelled') {
          return false;
        }
        
        return true;
      })
      setFilteredBookings(societyBookings)
      // Only update search results if there's no active search query
      // This prevents resetting search results when filteredBookings changes
      // The search effect will handle updating searchResults when searchQuery changes
      if (!searchQuery || searchQuery.trim() === '') {
        setSearchResults(societyBookings)
      }
    } else {
      // Clear results if no bookings or no active society
      setFilteredBookings([])
      setSearchResults([])
    }
  }, [bookings, activeSociety]) // Removed searchQuery from dependencies to prevent reset

  // Debounced search to prevent excessive filtering
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      const trimmedQuery = searchQuery.trim()
      
      // If search is empty, show all filtered bookings
      if (trimmedQuery === '') {
        setSearchResults(filteredBookings)
        return
      }
      
      // Perform search filtering
      const query = trimmedQuery.toLowerCase()
      const results = filteredBookings.filter(booking => {
        const plot = booking.plot_id
        const customer = booking.customer_id
        
        // Additional safety check: exclude available plots even in search
        if (plot?.status === 'Available' || plot?.booking_status === 'Available') {
          return false;
        }
        
        // Exclude cancelled bookings
        if (booking.booking_status === 'Cancelled') {
          return false;
        }
        
        // Convert plot number to string for comparison (handle both string and number)
        const plotNumber = plot?.plot_number ? String(plot.plot_number).trim() : ''
        const customerName = customer?.name ? String(customer.name).trim() : ''
        const customerCnic = customer?.contactInfo?.cnic ? String(customer.contactInfo.cnic).trim() : ''
        
        // Search in plot number, customer name, or CNIC (case-insensitive)
        const matchesPlotNumber = plotNumber.toLowerCase().includes(query)
        const matchesCustomerName = customerName.toLowerCase().includes(query)
        const matchesCnic = customerCnic.toLowerCase().includes(query)
        
        return matchesPlotNumber || matchesCustomerName || matchesCnic
      })
      
      setSearchResults(results)
    }, 150) // 150ms debounce

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery, filteredBookings])

  // --- LOGIC: Installment Fetching ---
  const fetchInstallments = async (bookingId) => {
    if (!bookingId) return
    setLoadingInstallments(true)
    try {
      const response = await getActiveInstallmentsByBooking(bookingId)
      const allInstallments = response?.installments || response || []
      const completed = allInstallments.filter(inst => inst.status === 'Completed')
      setCompletedInstallments(completed)
    } catch (err) {
      console.error('Error fetching installments:', err)
      setCompletedInstallments([])
    } finally {
      setLoadingInstallments(false)
    }
  }

  // --- LOGIC: Calculations ---
  // STRICT RULE: Total Paid = Initial Payment + Completed Installments (No overrides)
  useEffect(() => {
    if (selectedBooking) {
      const initialPayment = typeof selectedBooking.initial_payment === 'number' ? selectedBooking.initial_payment : 0

      const installmentsTotal = completedInstallments.reduce((sum, inst) => {
        return sum + (typeof inst.paid_amount === 'number' ? inst.paid_amount : 0)
      }, 0)

      const totalPaid = initialPayment + installmentsTotal
      const dealerCommission = selectedBooking.dealer_comission || 0

      let deduction = 0
      if (formData.societyDeductionType === 'percentage') {
        deduction = (totalPaid * (formData.societyDeductionPercentage || 0)) / 100
      } else {
        deduction = parseFloat(formData.societyDeductionAmount || 0)
      }

      const netRefund = Math.max(totalPaid - dealerCommission - deduction, 0)

      let monthly = 0
      let immediate = 0
      let remaining = 0

      if (formData.refundType === 'full') {
        immediate = netRefund
        setFormData(prev => ({ ...prev, initialRefundAmount: netRefund }))
      } else {
        immediate = parseFloat(formData.initialRefundAmount) || 0
        remaining = netRefund - immediate

        const months = (parseInt(formData.refundYears) * 12) + parseInt(formData.refundMonths)
        if (months > 0) monthly = remaining / months
      }

      setRefundDetails({
        totalPaid,
        dealerCommission,
        societyDeduction: deduction,
        netRefundAmount: netRefund,
        immediateRefund: immediate,
        remainingBalance: remaining,
        monthlyInstallment: monthly
      })
    }
  }, [selectedBooking, completedInstallments, formData])

  // --- HANDLERS ---
  const handleSelectBooking = (booking) => {
    setSelectedBooking(booking)
    setFormData(prev => ({
      ...prev,
      booking_id: booking._id,
      plotId: booking.plot_id?._id
    }))
    fetchInstallments(booking._id)
    // Don't clear search query - keep the filtered results visible
    // User might want to see other plots or change selection
  }

  const nextStep = () => {
    if (step === 1 && !selectedBooking) return
    setDirection(1)
    setStep(step + 1)
  }

  const prevStep = () => {
    setDirection(-1)
    setStep(step - 1)
  }

  const handleSubmit = async () => {
    setLoadingSubmit(true)
    setError(null)
    try {
      const submitData = {
        ...formData,
        societyId: activeSociety?._id,
        refundYears: parseInt(formData.refundYears || 0),
        refundMonths: parseInt(formData.refundMonths || 0),
        societyDeductionPercentage: parseFloat(formData.societyDeductionPercentage || 0),
        societyDeductionAmount: parseFloat(formData.societyDeductionAmount || 0),
        initialRefundAmount: parseFloat(formData.initialRefundAmount || 0),
      }

      await dispatch(cancelPlotThunk(submitData)).unwrap()
      onClose()
    } catch (err) {
      setError(err?.message || 'Failed to cancel plot')
    } finally {
      setLoadingSubmit(false)
    }
  }

  // --- UI HELPERS ---
  const formatCurrency = (val) => new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', minimumFractionDigits: 0 }).format(val || 0).replace('PKR', 'Rs.')

  const variants = {
    enter: (direction) => ({ x: direction > 0 ? 50 : -50, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction > 0 ? -50 : 50, opacity: 0 })
  }

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #475569;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
      `}</style>
      <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4'>
        <div className='bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex overflow-hidden border border-slate-200 dark:border-slate-800 flex-col md:flex-row'>

        {/* LEFT PANEL: WIZARD STEPS */}
        <div className='flex-1 flex flex-col h-full bg-slate-50 dark:bg-slate-900/50 relative'>
          {/* Header */}
          <div className='p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-white dark:bg-slate-900'>
            <div className='flex-1'>
              <h2 className='text-xl font-bold text-slate-800 dark:text-white mb-1'>Cancel Plot</h2>
              <div className='flex items-center gap-3'>
                <p className='text-xs text-slate-500'>Step {step} of 3</p>
                <div className='flex items-center gap-1'>
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`h-1.5 w-1.5 rounded-full transition-all ${
                        s === step
                          ? 'bg-teal-500 w-6'
                          : s < step
                          ? 'bg-teal-300 dark:bg-teal-600'
                          : 'bg-slate-300 dark:bg-slate-600'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <button 
              type="button" 
              onClick={onClose} 
              className='p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors'
              title="Close"
            >
              <X size={20} className='text-slate-500' />
            </button>
          </div>

          {/* Content Area */}
          <div className='flex-1 overflow-y-auto p-6 relative'>
            <AnimatePresence custom={direction} mode='wait'>

              {/* STEP 1: SELECT PLOT */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3 }}
                  className='space-y-6'
                >
                  {/* Instructions */}
                  <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4'>
                    <div className='flex items-start gap-3'>
                      <AlertCircle className='text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0' size={20} />
                      <div>
                        <h4 className='font-semibold text-blue-900 dark:text-blue-100 mb-1'>Step 1: Select Plot to Cancel</h4>
                        <p className='text-sm text-blue-700 dark:text-blue-300'>
                          Search for the plot you want to cancel by plot number, customer name, or CNIC. 
                          Select a plot to see the refund calculation on the right.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Search Input - Fixed to prevent refresh */}
                  <div className='relative'>
                    <Search className='absolute left-4 top-3.5 text-slate-400' size={20} />
                    <input
                      type="text"
                      placeholder='Type to search: Plot No, Customer Name, or CNIC...'
                      className='w-full pl-12 pr-10 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500 outline-none transition-all shadow-sm'
                      value={searchQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchQuery(value);
                      }}
                      onKeyDown={(e) => {
                        // Prevent form submission on Enter
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      autoFocus
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setSearchQuery('');
                        }}
                        className='absolute right-3 top-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>

                  {/* Results Section */}
                  <div className='space-y-3'>
                    <div className='flex items-center justify-between'>
                      <h3 className='text-sm font-semibold text-slate-500 uppercase tracking-wider'>
                        {searchQuery ? `Search Results (${searchResults.length})` : `Available Bookings (${searchResults.length})`}
                      </h3>
                      {selectedBooking && (
                        <span className='text-xs text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-2 py-1 rounded-full'>
                          Plot Selected ✓
                        </span>
                      )}
                    </div>
                    
                    <div className='grid grid-cols-1 gap-3 max-h-[400px] overflow-y-auto custom-scrollbar'>
                      {searchResults.length > 0 ? (
                        searchResults.map(b => (
                          <div
                            key={b._id}
                            onClick={() => handleSelectBooking(b)}
                            className={`p-4 rounded-xl border cursor-pointer transition-all flex items-center justify-between group ${
                              selectedBooking?._id === b._id
                                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 shadow-md ring-2 ring-teal-500/20'
                                : 'border-slate-200 dark:border-slate-700 hover:border-teal-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 bg-white dark:bg-slate-800'
                            }`}
                          >
                            <div className='flex items-center gap-4 flex-1'>
                              <div className={`h-12 w-12 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                                selectedBooking?._id === b._id
                                  ? 'bg-teal-500 text-white'
                                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 group-hover:bg-teal-100 dark:group-hover:bg-teal-900/30'
                              }`}>
                                {b.plot_id?.plot_number || '#'}
                              </div>
                              <div className='flex-1 min-w-0'>
                                <h4 className='font-semibold text-slate-800 dark:text-white truncate'>
                                  {b.plot_id?.plot_number ? `Plot ${b.plot_id.plot_number}` : 'No Plot #'}
                                </h4>
                                <p className='text-sm text-slate-500 dark:text-slate-400 truncate'>
                                  {b.customer_id?.name || 'Unknown Customer'}
                                </p>
                                {b.customer_id?.contactInfo?.cnic && (
                                  <p className='text-xs text-slate-400 dark:text-slate-500 mt-0.5'>
                                    CNIC: {b.customer_id.contactInfo.cnic}
                                  </p>
                                )}
                              </div>
                            </div>
                            {selectedBooking?._id === b._id && (
                              <div className='ml-4'>
                                <CheckCircleIcon />
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className='text-center py-12'>
                          <div className='inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 mb-4'>
                            <Search className='text-slate-400' size={24} />
                          </div>
                          <p className='text-slate-500 dark:text-slate-400 font-medium mb-1'>
                            {searchQuery ? 'No bookings found' : 'No bookings available'}
                          </p>
                          <p className='text-sm text-slate-400 dark:text-slate-500'>
                            {searchQuery 
                              ? 'Try a different search term or check the spelling'
                              : 'All plots in this society are already cancelled or available'
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* STEP 2: REFUND LOGIC */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className='space-y-6'
                >
                  {/* Instructions */}
                  <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4'>
                    <div className='flex items-start gap-3'>
                      <AlertCircle className='text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0' size={20} />
                      <div>
                        <h4 className='font-semibold text-amber-900 dark:text-amber-100 mb-1'>Step 2: Configure Refund Settings</h4>
                        <p className='text-sm text-amber-700 dark:text-amber-300'>
                          Choose how the refund will be processed and set any deductions. 
                          The refund calculation updates automatically on the right.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Refund Type Cards */}
                  <div>
                    <label className='text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 block'>
                      Refund Payment Method
                    </label>
                    <div className='grid grid-cols-2 gap-4'>
                      <TypeCard
                        selected={formData.refundType === 'full'}
                        onClick={() => setFormData({ ...formData, refundType: 'full' })}
                        icon={<DollarSign />}
                        title="Full Refund"
                        subtitle="One-time payment"
                        description="Customer receives entire refund amount immediately"
                      />
                      <TypeCard
                        selected={formData.refundType === 'installment'}
                        onClick={() => setFormData({ ...formData, refundType: 'installment' })}
                        icon={<Calendar />}
                        title="Installment Refund"
                        subtitle="Monthly payments"
                        description="Refund split into monthly installments"
                      />
                    </div>
                  </div>

                  {/* Options Inputs */}
                  <div className='bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 space-y-4 shadow-sm'>
                    <div className='flex items-center justify-between'>
                      <h3 className='font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2'>
                        <PieChart size={18} className='text-teal-500' /> Deduction Settings
                      </h3>
                      <span className='text-xs text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded'>
                        Required
                      </span>
                    </div>
                    <p className='text-xs text-slate-500 -mt-2'>
                      Set the deduction amount that will be subtracted from the total refund
                    </p>

                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <label className='text-xs font-medium text-slate-500 mb-1 block'>Type</label>
                        <select
                          className='w-full p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-teal-500 outline-none'
                          value={formData.societyDeductionType}
                          onChange={(e) => setFormData({ ...formData, societyDeductionType: e.target.value })}
                        >
                          <option value="percentage" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">Percentage (%)</option>
                          <option value="fixed" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">Fixed Amount</option>
                        </select>
                      </div>
                      <div>
                        <label className='text-xs font-medium text-slate-500 mb-1 block'>Value</label>
                        <input
                          type="number"
                          className='w-full p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-teal-500 outline-none'
                          placeholder={formData.societyDeductionType === 'percentage' ? 'e.g 10' : 'Amount'}
                          value={formData.societyDeductionType === 'percentage' ? formData.societyDeductionPercentage : formData.societyDeductionAmount}
                          onChange={(e) => setFormData({
                            ...formData,
                            societyDeductionPercentage: formData.societyDeductionType === 'percentage' ? e.target.value : 0,
                            societyDeductionAmount: formData.societyDeductionType === 'fixed' ? e.target.value : 0
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Installment Plan Inputs */}
                  {formData.refundType === 'installment' && (
                    <div className='bg-white dark:bg-slate-800 p-5 rounded-xl border border-slate-200 dark:border-slate-700 space-y-4 shadow-sm animate-in slide-in-from-top-4 fade-in duration-300'>
                      <div>
                        <h3 className='font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2'>
                          <Clock size={18} className='text-blue-500' /> Refund Plan
                        </h3>
                        <p className='text-xs text-slate-500 mt-1'>
                          Set the duration and upfront payment for installment refund
                        </p>
                      </div>

                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <label className='text-xs font-medium text-slate-500 mb-1 block'>Durations</label>
                          <div className='flex gap-2'>
                            <input
                              className='w-full p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-teal-500 outline-none'
                              placeholder="Years"
                              type="number"
                              value={formData.refundYears}
                              onChange={(e) => setFormData({ ...formData, refundYears: e.target.value })}
                            />
                            <input
                              className='w-full p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-teal-500 outline-none'
                              placeholder="Months"
                              type="number"
                              value={formData.refundMonths}
                              onChange={(e) => setFormData({ ...formData, refundMonths: e.target.value })}
                            />
                          </div>
                        </div>
                        <div>
                          <label className='text-xs font-medium text-slate-500 mb-1 block'>Upfront Payment</label>
                          <input
                            type="number"
                            className='w-full p-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-teal-500 outline-none'
                            placeholder='Initial Amount'
                            value={formData.initialRefundAmount}
                            onChange={(e) => setFormData({ ...formData, initialRefundAmount: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className='text-xs font-medium text-slate-500 mb-1 block'>Reason (Optional)</label>
                    <textarea
                      className='w-full p-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-teal-500 outline-none'
                      rows={2}
                      placeholder='Why is this plot being cancelled?'
                      value={formData.cancellationReason}
                      onChange={(e) => setFormData({ ...formData, cancellationReason: e.target.value })}
                    />
                  </div>
                </motion.div>
              )}

              {/* STEP 3: REVIEW */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className='space-y-6'
                >
                  {/* Instructions */}
                  <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4'>
                    <div className='flex items-start gap-3'>
                      <Check className='text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0' size={20} />
                      <div>
                        <h4 className='font-semibold text-green-900 dark:text-green-100 mb-1'>Step 3: Review & Confirm</h4>
                        <p className='text-sm text-green-700 dark:text-green-300'>
                          Review all details on the right. Once you confirm, the plot will be cancelled and the refund process will begin.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Summary Cards */}
                  <div className='grid grid-cols-1 gap-4'>
                    <div className='bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700'>
                      <div className='flex items-center gap-3 mb-3'>
                        <div className='h-10 w-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center'>
                          <MapPin className='text-teal-600 dark:text-teal-400' size={20} />
                        </div>
                        <div>
                          <h4 className='font-semibold text-slate-800 dark:text-white'>
                            {selectedBooking?.plot_id?.plot_number ? `Plot ${selectedBooking.plot_id.plot_number}` : 'Plot'}
                          </h4>
                          <p className='text-sm text-slate-500 dark:text-slate-400'>
                            {selectedBooking?.customer_id?.name || 'Customer'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className='bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700'>
                      <div className='flex items-center justify-between mb-2'>
                        <span className='text-sm text-slate-600 dark:text-slate-400'>Refund Type</span>
                        <span className='font-semibold text-slate-800 dark:text-white'>
                          {formData.refundType === 'full' ? 'Full Refund' : 'Installment Refund'}
                        </span>
                      </div>
                      {formData.refundType === 'installment' && (
                        <div className='flex items-center justify-between text-sm'>
                          <span className='text-slate-600 dark:text-slate-400'>Duration</span>
                          <span className='text-slate-800 dark:text-white'>
                            {(parseInt(formData.refundYears) * 12) + parseInt(formData.refundMonths)} months
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {error && (
                    <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 p-4 rounded-lg text-sm flex items-start gap-2'>
                      <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className='font-medium mb-1'>Error</p>
                        <p>{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Warning */}
                  <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4'>
                    <div className='flex items-start gap-3'>
                      <AlertCircle className='text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0' size={18} />
                      <div className='text-sm text-amber-700 dark:text-amber-300'>
                        <p className='font-medium mb-1'>Important</p>
                        <p>This action cannot be undone. The plot will be marked as available and all installments will be cancelled.</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>

          {/* Footer Buttons */}
          <div className='p-6 border-t border-slate-200 dark:border-slate-800 flex justify-between bg-white dark:bg-slate-900'>
            {step > 1 ? (
              <button type="button" onClick={prevStep} className='px-6 py-2 rounded-lg text-slate-600 hover:bg-slate-100 font-medium transition-colors'>
                Back
              </button>
            ) : (
              <div></div>
            )}

            {step < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={!selectedBooking}
                className='px-8 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium shadow-md transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Next <ChevronRight size={18} className='ml-1' />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loadingSubmit}
                className='px-8 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium shadow-lg transition-all flex items-center disabled:opacity-70'
              >
                {loadingSubmit ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                Confirm Cancellation
              </button>
            )}
          </div>
        </div>

        {/* RIGHT PANEL: LIVE RECEIPT */}
        <div className='w-full md:w-[350px] bg-slate-100 dark:bg-slate-950 border-l border-slate-200 dark:border-slate-800 flex flex-col h-full overflow-hidden'>
          {/* Fixed Header */}
          <div className='p-6 pb-4 flex-shrink-0 border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-950'>
            <h3 className='text-sm font-bold text-slate-400 uppercase tracking-widest mb-2'>Pro-Forma Receipt</h3>
            <p className='text-xs text-slate-500 dark:text-slate-400'>
              Live calculation preview
            </p>
          </div>

          {/* Scrollable Content */}
          <div className='flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar'>
            {/* 1. Plot Info */}
            {selectedBooking ? (
              <div className='bg-white dark:bg-slate-900 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800'>
                <div className='flex justify-between items-start mb-2'>
                  <span className='text-xs text-slate-400 dark:text-slate-500'>Plot Reference</span>
                  <span className='text-xs font-mono font-bold bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 px-2 py-0.5 rounded'>
                    {selectedBooking?.plot_id?.plot_number || '---'}
                  </span>
                </div>
                <div className='text-sm font-medium text-slate-800 dark:text-white truncate mb-1'>
                  {selectedBooking?.customer_id?.name || 'Unknown'}
                </div>
                {selectedBooking?.customer_id?.contactInfo?.cnic && (
                  <div className='text-xs text-slate-500 dark:text-slate-400'>
                    CNIC: {selectedBooking.customer_id.contactInfo.cnic}
                  </div>
                )}
              </div>
            ) : (
              <div className='bg-slate-50 dark:bg-slate-900/50 p-6 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-center'>
                <MapPin className='mx-auto text-slate-400 mb-2' size={24} />
                <p className='text-sm text-slate-500 dark:text-slate-400'>
                  Select a plot to see refund calculation
                </p>
              </div>
            )}

            {/* 2. Calculation Breakdown - Enhanced with clear sections */}
            {selectedBooking && (
              <div className='bg-white dark:bg-slate-900 p-5 rounded-xl shadow-md border-2 border-slate-200 dark:border-slate-700 space-y-4 receipt-lines'>
                {/* Income Section */}
                <div className='space-y-3'>
                  <div className='flex items-center gap-2 mb-3 pb-2 border-b-2 border-green-200 dark:border-green-800'>
                    <div className='h-2 w-2 rounded-full bg-green-500'></div>
                    <div className='text-sm font-bold text-green-700 dark:text-green-400 uppercase tracking-wider'>Amounts Received</div>
                  </div>
                  <ReceiptLine label="Initial Payment" amount={selectedBooking?.initial_payment || 0} />
                  <ReceiptLine
                    label={`Installments Paid (${completedInstallments.length})`}
                    amount={refundDetails.totalPaid - (selectedBooking?.initial_payment || 0)}
                    highlight={loadingInstallments}
                  />
                  <div className='border-t-2 border-dashed border-slate-400 dark:border-slate-600 my-3'></div>
                  <ReceiptLine label="Total Amount Received" amount={refundDetails.totalPaid} bold />
                </div>
                
                {/* Deductions Section */}
                <div className='space-y-3 pt-4 border-t-2 border-slate-300 dark:border-slate-600'>
                  <div className='flex items-center gap-2 mb-3 pb-2 border-b-2 border-rose-200 dark:border-rose-800'>
                    <div className='h-2 w-2 rounded-full bg-rose-500'></div>
                    <div className='text-sm font-bold text-rose-600 dark:text-rose-400 uppercase tracking-wider'>Deductions</div>
                  </div>
                  <ReceiptLine 
                    label="Dealer Commission" 
                    amount={-refundDetails.dealerCommission} 
                    isDeduction 
                    tooltip="Commission paid to dealer at time of booking"
                  />
                  <ReceiptLine 
                    label={`Society Deduction ${formData.societyDeductionType === 'percentage' ? `(${formData.societyDeductionPercentage || 0}%)` : '(Fixed)'}`}
                    amount={-refundDetails.societyDeduction} 
                    isDeduction 
                    tooltip={formData.societyDeductionType === 'percentage' 
                      ? `Percentage deduction: ${formData.societyDeductionPercentage || 0}% of total paid`
                      : 'Fixed amount deduction as per policy'}
                  />
                  <div className='border-t-2 border-dashed border-slate-400 dark:border-slate-600 my-3'></div>
                  <ReceiptLine 
                    label="Total Deductions" 
                    amount={-(refundDetails.dealerCommission + refundDetails.societyDeduction)} 
                    isDeduction 
                    bold 
                  />
                </div>
              </div>
            )}

            {/* 3. Net Total - Enhanced with breakdown */}
            {selectedBooking ? (
              <div className='bg-gradient-to-br from-teal-600 to-teal-700 text-white p-5 rounded-xl shadow-lg shadow-teal-500/20 mt-4'>
                <div className='text-teal-100 text-xs mb-1 font-medium'>Net Refund Amount</div>
                <div className='text-2xl font-bold tracking-tight mb-2'>
                  {formatCurrency(refundDetails.netRefundAmount)}
                </div>
                {formData.refundType === 'installment' && refundDetails.remainingBalance > 0 && (
                  <div className='text-xs text-teal-100 pt-2 border-t border-teal-500/30 mt-2 space-y-1'>
                    <div className='flex justify-between'>
                      <span>Immediate Refund:</span>
                      <span className='font-semibold'>{formatCurrency(refundDetails.immediateRefund)}</span>
                    </div>
                    <div className='flex justify-between'>
                      <span>Remaining Balance:</span>
                      <span className='font-semibold'>{formatCurrency(refundDetails.remainingBalance)}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className='bg-slate-200 dark:bg-slate-800 p-5 rounded-xl mt-4 text-center'>
                <div className='text-slate-400 dark:text-slate-500 text-xs mb-1'>Net Refund Amount</div>
                <div className='text-xl font-bold text-slate-300 dark:text-slate-600'>
                  Rs. 0
                </div>
              </div>
            )}

            {/* Refund Schedule Preview */}
            {selectedBooking && formData.refundType === 'installment' && refundDetails.monthlyInstallment > 0 && (
              <div className='bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border-2 border-blue-300 dark:border-blue-700'>
                <div className='flex items-center gap-2 mb-4 pb-2 border-b-2 border-blue-300 dark:border-blue-700'>
                  <Calendar className='text-blue-600 dark:text-blue-400' size={18} />
                  <span className='text-sm font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider'>Refund Schedule</span>
                </div>
                <div className='space-y-3 text-sm'>
                  <div className='flex justify-between items-center py-1.5'>
                    <span className='font-semibold text-slate-800 dark:text-slate-200'>Monthly Payment:</span>
                    <span className='font-bold text-base text-blue-700 dark:text-blue-400'>{formatCurrency(refundDetails.monthlyInstallment)}</span>
                  </div>
                  <div className='flex justify-between items-center py-1.5'>
                    <span className='font-semibold text-slate-800 dark:text-slate-200'>Total Duration:</span>
                    <span className='text-slate-900 dark:text-slate-100 font-bold'>
                      {(parseInt(formData.refundYears) * 12) + parseInt(formData.refundMonths)} Months
                    </span>
                  </div>
                  {refundDetails.immediateRefund > 0 && (
                    <div className='flex justify-between items-center pt-3 mt-3 border-t-2 border-blue-300 dark:border-blue-700'>
                      <span className='font-semibold text-slate-800 dark:text-slate-200'>Upfront Payment:</span>
                      <span className='font-bold text-base text-blue-700 dark:text-blue-400'>{formatCurrency(refundDetails.immediateRefund)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Fixed Footer */}
          <div className='p-6 pt-4 flex-shrink-0 border-t border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-950'>
            <p className='text-[10px] text-slate-400 dark:text-slate-500 text-center leading-relaxed'>
              💡 Calculations are automatic based on verified transaction history. 
              All amounts are in PKR (Pakistani Rupees).
            </p>
          </div>
        </div>

        </div>
      </div>
    </>
  )
}

// --- SUB-COMPONENTS ---

const TypeCard = ({ selected, onClick, icon, title, subtitle, description }) => (
  <div
    onClick={onClick}
    className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${
      selected
        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 shadow-md ring-2 ring-teal-500/20'
        : 'border-slate-200 dark:border-slate-700 hover:border-teal-200 hover:bg-slate-50 dark:hover:bg-slate-800/50 bg-white dark:bg-slate-800'
    }`}
  >
    {selected && (
      <div className='absolute top-2 right-2'>
        <div className='h-5 w-5 rounded-full bg-teal-500 flex items-center justify-center'>
          <Check size={12} className='text-white' />
        </div>
      </div>
    )}
    <div className={`mb-3 transition-colors ${selected ? 'text-teal-600 dark:text-teal-400' : 'text-slate-400'}`}>
      {icon}
    </div>
    <h4 className={`font-bold mb-1 ${selected ? 'text-teal-700 dark:text-teal-300' : 'text-slate-600 dark:text-slate-300'}`}>
      {title}
    </h4>
    <p className='text-xs text-slate-500 dark:text-slate-400 mb-1'>{subtitle}</p>
    {description && (
      <p className='text-xs text-slate-400 dark:text-slate-500 mt-2'>{description}</p>
    )}
  </div>
)

const ReceiptLine = ({ label, amount, isDeduction, bold, highlight, tooltip }) => {
  const formatCurrency = (val) => new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', minimumFractionDigits: 0 }).format(val || 0).replace('PKR', 'Rs.')
  return (
    <div className={`flex justify-between items-center py-1.5 ${bold ? 'text-base font-bold text-slate-900 dark:text-white' : 'text-sm font-medium text-slate-800 dark:text-slate-200'} group relative`}>
      <span className={tooltip ? 'cursor-help underline decoration-dotted' : ''} title={tooltip}>{label}</span>
      {highlight ? (
        <span className='bg-slate-200 dark:bg-slate-700 animate-pulse h-5 w-20 rounded'></span>
      ) : (
        <span className={`${isDeduction ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-slate-900 dark:text-slate-100'} ${bold ? 'font-bold text-lg' : 'font-semibold'}`}>
          {formatCurrency(amount)}
        </span>
      )}
    </div>
  )
}

const CheckCircleIcon = () => (
  <div className='h-6 w-6 rounded-full bg-teal-500 flex items-center justify-center'>
    <Check size={14} className='text-white' />
  </div>
)

export default CancelPlotForm
